<template>
  <section class="shared-section">
    <header class="shared-section-header">
      <h2 class="pl-4 shared-section-header-heading sm:pl-0">Sales Reps</h2>
    </header>

    <!-- Table -->
    <Table
      toolbarHeading="Sales Reps List"
      :withToolbar="true"
      :withSearch="true"
      :withPagination="true"
      :tColumns="$options.tColumns"
      :tData="salesReps"
      :fetch="fetchSalesReps"
    >
      <template #table-toolbar>
        <button type="button" class="shared-table-toolbar-button" @click="showModal = true">
          <VSvgIcon name="plus" class="h-5 mr-1" /> Add Sales Rep
        </button>
      </template>

      <template #table-data-actions="trData">
        <td class="shared-table-cells-cell">
          <div class="flex justify-center">
            <button
              class="mr-2 shared-table-cells-button round edit"
              @click="() => handleSelectEditableRep(trData)"
            >
              <VSvgIcon name="pencil" class="h-5" />
            </button>
            <button
              class="shared-table-cells-button round delete"
              @click="handleDeleteRep(trData.id)"
            >
              <VSvgIcon name="trash" class="h-5" />
            </button>
          </div>
        </td>
      </template>
    </Table>

    <!-- Modals -->
    <ModalPlus
      :title="editable ? 'Edit Sale Rep' : 'Add Sale Rep'"
      :buttonText="editable ? 'Save' : 'Add'"
      :show="showModal"
      :onClose="handleClose"
      :onProceed="handleSubmit(handleProceed)"
      :loading="formLoading"
      maxWidth="800px"
    >
      <div class="py-8 pb-4">
        <SalesRepForm
          :accountCoordinator="accountCoordinator"
          :errors="errors"
          :initialValues="editableRep"
          @set="handleSetField"
        />
      </div>
    </ModalPlus>
  </section>
</template>

<script>
import { mapState } from 'vuex';

import { safeNullDataset, isPhoneNum, isEmail } from '@/utils';

import { formMixin } from '@/mixins';

import VSvgIcon from '@/components/base/VSvgIcon';
import ModalPlus from '@/components/shared/modal/ModalPlus';
import Table from '@/components/shared/table/Table';
import SalesRepForm from '@/components/admin/SalesRepForm';

export default {
  name: 'SalesReps',
  components: { VSvgIcon, ModalPlus, Table, SalesRepForm },
  mixins: [
    formMixin(
      {
        name: '',
        email: '',
        phone: '',
        is_coordinator: 0,
        sales_rep_id: '',
      },
      ['sales_rep_id', 'is_coordinator'],
      [],
      [
        {
          field: 'phone',
          msg: 'Invalid phone number.',
          predicateFn: phone => !isPhoneNum(phone),
        },
        {
          field: 'email',
          msg: 'Invalid email address.',
          predicateFn: email => !isEmail(email),
        },
      ],
    ),
  ],
  data() {
    return {
      showModal: false,
      editableRep: {},
      accountCoordinator: [],
    };
  },
  computed: mapState({
    salesReps: state => ({
      data: safeNullDataset(state)(['admin', 'salesReps', 'data']).map(rep => ({
        name: rep.name || '-',
        email: rep.email || '-',
        phone: rep.phone || '-',
        'is coordinator': rep.is_coordinator ? 'YES' : 'NO',
        'sale coordinator': rep.sales_rep_id ? rep.sales_rep_id.name : null,
        ...rep,
      })),
      meta: state.admin.salesReps.meta,
    }),
    editable() {
      return !!Object.keys(this.editableRep).length;
    },
  }),
  methods: {
    async fetchSalesReps(params) {
      return await this.$store.dispatch('admin/getSalesReps', params);
    },
    handleSelectEditableRep(rep) {
      this.editableRep = rep;
      this.showModal = true;
    },
    handleClose() {
      this.handleResetFields();
      this.handleResetErrors();
      this.editableRep = {};
      this.showModal = false;
    },
    async handleProceed(data) {
      this.editable ? await this.handleUpdate(data) : await this.handleAdd(data);
      this.handleClose();
    },
    async handleUpdate(data) {
      await this.handleUpdateTag(data);
    },
    async handleUpdateTag(data) {
      const { id } = this.editableRep;
      data.sales_rep_id = data.sales_rep_id ? data.sales_rep_id.id : null;
      await this.$store.dispatch('admin/updateSaleRep', { id, data: data });
    },
    async handleAddRep(data) {
      data.sales_rep_id = data.sales_rep_id ? data.sales_rep_id.id : null;
      await this.$store.dispatch('admin/addSaleRep', { data: data });
    },
    async handleAdd(data) {
      await this.handleAddRep(data);
    },
    async handleDeleteRep(id) {
      try {
        await this.$store.dispatch('admin/deleteSaleRep', id);
      } catch (error) {
        return this.$notify({
          group: 'error',
          type: 'error',
          title: error.response.statusText,
          text: error.response.data.message,
        });
      }
    },
  },
  async mounted() {
    const { data } = await this.$http.get('/admin/account-coordinator');
    this.accountCoordinator = data;
  },
  tColumns: [],
  created() {
    this.$options.tColumns = [
      { column: 'name', isSort: true, orderBy: 'name' },
      { column: 'email', isSort: false },
      { column: 'phone', isSort: false },
      { column: 'is coordinator', isSort: false },
      { column: 'sale coordinator', isSort: false },
      { column: 'actions', isSort: false, classes: { th: 'w-32', td: 'w-32' } },
    ];
  },
};
</script>
